<template>
  <div class="product__option product__buy-more-save-more-pack">
    <template v-if="product">
      <h2 v-if="productPackDiscount?.length">
        Buy more save more (can be applied for pieces with different
        customizations)
      </h2>
    </template>
    <sib-skeleton-loading v-else width="12em"></sib-skeleton-loading>

    <template v-if="product">
      <div class="content" v-if="productPackDiscount?.length">
        <template
          v-for="(item, index) of productPackDiscountDetails"
          :key="index"
        >
          <div class="discount-line">
            <div>{{ `Buy ${item.numberPack} pieces` }}</div>
            <div>
              <span class="price-final">
                {{ formatPrice(item.price) }}
              </span>
              <span v-if="item.comparePrice" class="price-compare ml-xs">
                {{ formatPrice(item.comparePrice) }}
              </span>
            </div>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="content">
        <sib-skeleton-loading
          :class="['mb-sm', index <= 2 ? 'mt-sm' : '']"
          height="1.5em"
          width="5em"
          v-for="index in 6"
          :key="index"
        ></sib-skeleton-loading>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import useProductStore from '@/store/product'
import {
  CAMPAIGN_DISCOUNT_BLACKFRIDAY9,
  PACK_OPTIONS,
  PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE,
  TYPE_OPTIONS,
  SIZE_OPTIONS,
} from '@/utils'
import { formatPrice } from '@/utils/product'
import useDiscountStore from '@/store/discount'
import { computed } from 'vue'

const productStore = useProductStore()
const discountStore = useDiscountStore()

const product = computed(() => productStore.product)

const isCampaignDiscountBlackfriday9 = computed(
  () =>
    discountStore.campaignDiscountCode?.toLowerCase() ==
    CAMPAIGN_DISCOUNT_BLACKFRIDAY9
)

const selectedVariant = computed(() => {
  return productStore.selectedVariant
})

const productPackDiscount = computed(() => {
  if (!product.value) return []
  const index = product.value.options?.findIndex((item) =>
    item.name?.includes(PACK_OPTIONS)
  )
  if (index != -1) return product.value.options[index].values?.slice(1)
  return []
})

const productPackDiscountDetails = computed(() => {
  if (!product.value || !productPackDiscount.value?.length) return []
  return productPackDiscount.value.map((packItem) => ({
    packItem,
    numberPack: getNumberPack(packItem),
    price: getPrice(packItem),
    comparePrice: getComparePrice(packItem),
  }))
})

function getSelectedOption(name: string = '') {
  if (!selectedVariant.value?.selectedOptions) return ''
  const selectedOption = selectedVariant.value.selectedOptions.find(
    (o) => o.name === name
  )
  if (!selectedOption) return ''
  return selectedOption.value
}

function getVariantProductByTypeAndPackOption(
  typeSelected: string,
  sizeSelected: string,
  packOption: string
) {
  if (!packOption) return
  if (!product.value?.variants) return

  const variantProductByTypeAndPackOption = product.value.variants.find(
    (item) => {
      if (!item.selectedOptions?.length) return false
      const indexPackOption = item.selectedOptions.findIndex(
        (option) => option.name == PACK_OPTIONS && option.value == packOption
      )
      if (typeSelected && sizeSelected) {
        const indexTypeOption = item.selectedOptions.findIndex(
          (option) =>
            option.name == TYPE_OPTIONS && option.value == typeSelected
        )
        const indexSizeOption = item.selectedOptions.findIndex(
          (option) =>
            option.name == SIZE_OPTIONS && option.value == sizeSelected
        )
        if (
          indexTypeOption != -1 &&
          indexSizeOption != -1 &&
          indexPackOption != -1
        )
          return true
      } else if (typeSelected) {
        const indexTypeOption = item.selectedOptions.findIndex(
          (option) =>
            option.name == TYPE_OPTIONS && option.value == typeSelected
        )
        if (indexTypeOption != -1 && indexPackOption != -1) return true
      } else if (sizeSelected) {
        const indexSizeOption = item.selectedOptions.findIndex(
          (option) =>
            option.name == SIZE_OPTIONS && option.value == sizeSelected
        )
        if (indexSizeOption != -1 && indexPackOption != -1) return true
      } else {
        if (indexPackOption != -1) return true
      }
      return false
    }
  )

  return variantProductByTypeAndPackOption
}

function getPrice(packOption: string) {
  const typeSelected = getSelectedOption(TYPE_OPTIONS)
  const sizeSelected = getSelectedOption(SIZE_OPTIONS)
  const variantProduct = getVariantProductByTypeAndPackOption(
    typeSelected,
    sizeSelected,
    packOption
  )
  const price = variantProduct?.priceV2?.amount || 0
  return isCampaignDiscountBlackfriday9.value
    ? price * ((100 - PERCENTAGE_DISCOUNT_BF9_FOR_PACK_OPTION_PERCENTAGE) / 100)
    : price
}

function getComparePrice(packOption: string) {
  const typeSelected = getSelectedOption(TYPE_OPTIONS)
  const sizeSelected = getSelectedOption(SIZE_OPTIONS)
  const variantProduct = getVariantProductByTypeAndPackOption(
    typeSelected,
    sizeSelected,
    packOption
  )
  if (variantProduct?.compareAtPriceV2?.amount) {
    return variantProduct.compareAtPriceV2.amount
  }

  const number = getNumberPack(packOption)

  if (!isNaN(number)) {
    return number * (product.value?.price || 0)
  }
  return 0
}

function getNumberPack(pack: string) {
  if (!pack) return 0
  return +pack.split('-')[0]?.split(' ')[1] || 0
}
</script>

<style lang="scss">
.product__buy-more-save-more-pack {
  .content {
    display: grid;
    grid-template-columns: 1fr;

    .discount-line {
      padding: 0.5em 0;
      display: grid;
      grid-template-columns: 1fr 1fr;
      border-bottom: solid 1px var(--border-color);
    }

    @include media-md-up {
      .discount-line {
        grid-template-columns: 100px 1fr;
      }
    }

    @include media-lg-up {
      .discount-line {
        grid-template-columns: 150px 1fr;
      }
    }

    @include media-xl-up {
      .discount-line {
        grid-template-columns: 200px 1fr;
      }
    }

    .price-final {
      font-weight: 600;
    }

    .price-compare {
      text-decoration: line-through;
      color: var(--text-color-tertiary);
    }
  }
}
</style>
